import axios, { AxiosInstance } from "axios";
import { Constants } from "../utils/constants";
import logger from "../utils/logger";


export interface ApiResponse<T = any> {
  data: T;
  status: number;
  message?: string;
}

class BillingService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: Constants.BILLING_API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add request interceptor to handle auth with fallback like messagingService
    this.api.interceptors.request.use(config => {
      let token = localStorage.getItem("authToken");
      if (!token) {
        token = localStorage.getItem("access_token");
      }
      if (token) {
        const hasBearer = token.toLowerCase().startsWith("bearer ");
        config.headers.Authorization = hasBearer ? token : `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      response => response,
      error => {
        logger.error("Billing API Error:", error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  }

  // ========================================
  // CURRENCY MANAGEMENT
  // ========================================
  public async getCurrencies(params?: {
    code?: string;
    active?: boolean;
    isDefault?: boolean;
    page?: number;
    limit?: number;
    orderBy?: "createdAt:asc" | "createdAt:desc" | "name:asc" | "name:desc";
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/currencies", { params });
  }

  public async getCurrencyById(id: number): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/currencies/${id}`);
  }

  public async getCurrencyByCode(code: string): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/currencies/code/${code}`);
  }

  public async searchCurrencies(params: {
    q: string;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/currencies/search", { params });
  }

  public async createCurrency(payload: {
    code: string;
    name: string;
    symbol: string;
    active?: boolean;
    isDefault?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/currencies", payload);
  }

  public async updateCurrency(
    id: number,
    payload: {
      code?: string;
      name?: string;
      symbol?: string;
      active?: boolean;
      isDefault?: boolean;
    }
  ): Promise<ApiResponse<any>> {
    return this.api.patch(`/api/v1/currencies/${id}`, payload);
  }

  public async deleteCurrency(id: number): Promise<ApiResponse<any>> {
    return this.api.delete(`/api/v1/currencies/${id}`);
  }

  // ========================================
  // DISCOUNT MANAGEMENT
  // ========================================
  public async getDiscounts(params?: {
    organizationId?: number;
    code?: string;
    active?: boolean;
    page?: number;
    limit?: number;
    orderBy?: "createdAt:asc" | "createdAt:desc" | "name:asc" | "name:desc";
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/discounts", { params });
  }

  public async getDiscountById(id: number): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/discounts/${id}`);
  }

  public async getDiscountByCode(code: string): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/discounts/code/${code}`);
  }

  public async searchDiscounts(params: {
    q: string;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/discounts/search", { params });
  }

  public async createDiscount(payload: {
    organizationId: number;
    code: string;
    name: string;
    description: string;
    active: boolean;
    percentage?: number;
    fixedValue?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/discounts", payload);
  }

  public async updateDiscount(
    id: number,
    payload: {
      code?: string;
      name?: string;
      description?: string;
      active?: boolean;
      percentage?: number;
      fixedValue?: number;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<ApiResponse<any>> {
    return this.api.patch(`/api/v1/discounts/${id}`, payload);
  }

  public async deleteDiscount(id: number): Promise<ApiResponse<any>> {
    return this.api.delete(`/api/v1/discounts/${id}`);
  }

  // ========================================
  // PACKAGE MANAGEMENT (Business Rule: Admin=STANDARD, Host=CUSTOM)
  // ========================================
  public async getPackages(params?: {
    type?: "STANDARD" | "CUSTOM";
    discountId?: number;
    currencyId?: number;
    recurring?: boolean;
    id?: string;
    page?: number;
    limit?: number;
    orderBy?:
      | "createdAt:asc"
      | "createdAt:desc"
      | "code:asc"
      | "code:desc"
      | "name:asc"
      | "name:desc"
      | "amount:asc"
      | "amount:desc";
  }): Promise<ApiResponse<any>> {
    if (params?.id) {
      return this.api.get(`/api/v1/packages/${params.id}`);
    }
    return this.api.get("/api/v1/packages", { params });
  }

  public async getApplicablePackages(params?: {
    type?: "STANDARD" | "CUSTOM";
    discountId?: number;
    currencyId?: number;
    recurring?: boolean;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/packages/applicable", { params });
  }

  public async getApplicablePackageById(id: string): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/packages/${id}/applicable`);
  }

  public async createPackage(payload: {
    providerId?: number | null; // REQUIRED but nullable
    code: string;
    name: string;
    description: string;
    type: "STANDARD" | "CUSTOM"; // Business rule: Admin=STANDARD, Host=CUSTOM
    version: string;
    discountId?: number; // Optional discount (Business rule)
    maximumServiceProvidersAllowed: number;
    maximumTeamSize: number;
    currencyId: number;
    // Note: amount field removed - not in create schema, only in update
    validity?: number;
    validityTimeUnit?: "HOUR" | "DAY" | "MONTH";
    expiryDate?: string;
    recurring: boolean;
    offerIds: number[]; // Business rule: Package has offers
    orgTypeIds: number[];
    customerOrgIds?: number[]; // Optional
  }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/packages", payload);
  }

  public async createHostPackage(payload: {
    name: string;
    code: string;
    description: string;
    currencyId: number;
    type: "STANDARD" | "CUSTOM";
    version: string;
    discountId?: number;
    maximumServiceProvidersAllowed: number;
    maximumTeamSize: number;
    validity?: number;
    validityTimeUnit?: "HOUR" | "DAY" | "MONTH";
    expiryDate?: string;
    recurring: boolean;
    offerIds: number[];
    customerOrgIds?: number[];
  }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/packages/me", payload);
  }

  public async searchPackages(params: {
    q: string;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/packages/search", { params });
  }

  public async updatePackage(
    id: string,
    payload: {
      code?: string;
      name?: string;
      description?: string;
      type?: "STANDARD" | "CUSTOM";
      version?: string;
      discountId?: number;
      maximumServiceProvidersAllowed?: number;
      maximumTeamSize?: number;
      currencyId?: number;
      amount?: number; // Note: amount IS present in update schema (not in create)
      validity?: number;
      validityTimeUnit?: "HOUR" | "DAY" | "MONTH";
      expiryDate?: string;
      recurring?: boolean;
      linkedOfferIds?: number[];
      unlinkedOfferIds?: number[];
      linkedOrgTypeIds?: number[];
      unlinkedOrgTypeIds?: number[];
      linkedCustomerOrgIds?: number[]; // Supported by API
      unlinkedCustomerOrgIds?: number[]; // Supported by API
    }
  ): Promise<ApiResponse<any>> {
    return this.api.patch(`/api/v1/packages/${id}`, payload);
  }

  public async deletePackage(id: string): Promise<ApiResponse<any>> {
    return this.api.delete(`/api/v1/packages/${id}`);
  }

  // ========================================
  // OFFER MANAGEMENT (Business Rule: Package has offers)
  // ========================================
  public async getOffers(params?: {
    serviceCategoryId?: number;
    serviceSubcategoryId?: number;
    serviceTier?: "LOW" | "MEDIUM" | "HIGH";
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/offers", { params });
  }

  public async getOfferById(id: number): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/offers/${id}`);
  }

  public async searchOffers(params: {
    q: string;
    page?: number;
    limit?: number;
    orderBy?: string;
    organizationId?: number;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/offers/search", { params });
  }

  public async createOffer(payload: {
    organizationId: number;
    providerId?: number | null;
    code: string;
    name: string;
    description: string;
    serviceSubcategoryId: number;
    durationUnit: "HOUR" | "DAY" | "MONTH";
    minimumDuration: number;
    maximumDuration?: number;
    maximumCheckIns: number; // REQUIRED field
    numberOfServiceProviders: number;
    serviceTier?: "LOW" | "MEDIUM" | "HIGH";
    currencyId: number; // REQUIRED - price field
    unitPrice: number; // REQUIRED - price field
    packageIds: string[]; // REQUIRED - Business rule: Offers linked to packages
  }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/offers", payload);
  }

  public async updateOffer(
    id: number,
    payload: {
      providerId?: number | null;
      code?: string;
      name?: string;
      description?: string;
      serviceSubcategoryId?: number;
      durationUnit?: "HOUR" | "DAY" | "MONTH";
      minimumDuration?: number;
      maximumDuration?: number;
      maximumCheckIns?: number;
      numberOfServiceProviders?: number;
      serviceTier?: "LOW" | "MEDIUM" | "HIGH";
      currencyId?: number; // Price field
      unitPrice?: number; // Price field
      linkedPackageIds?: string[];
      unlinkedPackageIds?: string[];
    }
  ): Promise<ApiResponse<any>> {
    return this.api.patch(`/api/v1/offers/${id}`, payload);
  }

  public async deleteOffer(id: number): Promise<ApiResponse<any>> {
    return this.api.delete(`/api/v1/offers/${id}`);
  }

  // ========================================
  // SUBSCRIPTION MANAGEMENT (Business Rule: User always buys subscription)
  // ========================================
  // Change organization subscription (update to new package)
  public async changeOrganizationSubscription(params: {
    organizationId: number;
    subscriptionId: number;
    packageId: string;
    quantity: number;
  }): Promise<ApiResponse<any>> {
    const { organizationId, subscriptionId, packageId, quantity } = params;
    return this.api.post(`/api/v1/organizations/${organizationId}/subscriptions/${subscriptionId}/change`, {
      packageId,
      quantity,
    });
  }

  // Legacy method for backward compatibility
  public async changeOrganizationSubscriptionTier(payload: {
    organizationId: number;
    servicePackageId: string; // Business rule: Subscription has package
  }): Promise<ApiResponse<any>> {
    // This method is deprecated - use changeOrganizationSubscription instead
    logger.warn("changeOrganizationSubscriptionTier is deprecated. Use changeOrganizationSubscription instead.");
    return this.api.post("/api/v1/subscriptions/change", payload);
  }

  // Get organization subscriptions
  public async getOrganizationSubscriptions(params: {
    organizationId: number;
    packageId?: number;
    subscriptionDate_gte?: string;
    subscriptionDate_lte?: string;
    purchaserId?: string;
    page?: number;
    limit?: number;
    orderBy?: "subscriptionDate:asc" | "subscriptionDate:desc";
    status?: string;
  }): Promise<ApiResponse<any>> {
    const { organizationId, ...queryParams } = params;
    return this.api.get(`/api/v1/organizations/${organizationId}/subscriptions`, {
      params: queryParams,
    });
  }

  // New: Create subscription for an organization using packageId and quantity
  public async createOrganizationSubscription(params: {
    organizationId: number;
    packageId: string;
    quantity: number;
  }): Promise<ApiResponse<any>> {
    const { organizationId, packageId, quantity } = params;
    return this.api.post(`/api/v1/organizations/${organizationId}/subscriptions`, {
      packageId,
      quantity,
    });
  }

  // Cancel organization subscription
  public async cancelOrganizationSubscription(params: {
    organizationId: number;
    subscriptionId: number;
    reason?: string;
  }): Promise<ApiResponse<any>> {
    const { organizationId, subscriptionId } = params;
    // Updated to use PUT method as per API specification
    return this.api.put(`/api/v1/organizations/${organizationId}/subscriptions/${subscriptionId}/cancel`);
  }

  // ========================================
  // INVOICE MANAGEMENT (Business Rule: USD currency)
  // ========================================
  public async getInvoices(params?: {
    organizationId?: number;
    providerId?: number;
    organizationTypeId?: number;
    clients?: boolean;
    referenceCode?: string;
    clientReference?: string; // Booking ID or other client reference
    status?: "PAID" | "PENDING" | "CANCELLED";
    productType?: "SUBSCRIPTION" | "OTHER" | "DIRECT_BOOKING";
    includeRelations?: boolean;
    page?: number;
    limit?: number;
    orderBy?: "createdAt:asc" | "createdAt:desc";
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/invoices", { params });
  }

  public async getInvoiceById(id: number): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/invoices/${id}`);
  }

  public async searchInvoices(params: {
    q?: string;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/invoices/search", { params });
  }

  public async createInvoice(payload: {
    organizationId: number;
    providerId?: number;
    clientReference?: string; // Booking ID or other reference
    description: string;
    currencyId?: number;
    invoiceItems: Array<{
      amount: string;
      unitPrice?: string;
      quantity?: number;
      description: string;
    }>;
    productType: "SUBSCRIPTION" | "OTHER" | "DIRECT_BOOKING";
    serviceDate: string;
    servicePackageId?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/invoices", payload);
  }

  public async updateInvoice(
    id: number,
    payload: {
      statusLastModifiedAt?: string;
      status?: "PAID" | "PENDING" | "CANCELLED";
      paymentCompletedAt?: string;
      description?: string;
    }
  ): Promise<ApiResponse<any>> {
    return this.api.patch(`/api/v1/invoices/${id}`, payload);
  }

  public async initiatePayment(payload: { reference: string; providerId?: number | null }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/invoices/initiate-payment", payload);
  }

  public async cancelInvoice(payload: { reference: string; narration: string }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/invoices/cancel", payload);
  }

  // ========================================
  // TRANSACTION MANAGEMENT (Business Rule: USD currency)
  // ========================================
  public async getTransactions(params?: {
    organizationId?: number;
    invoiceId?: number;
    type?: boolean;
    createdAt_gte?: string;
    createdAt_lte?: string;
    page?: number;
    limit?: number;
    orderBy?: "createdAt:asc" | "createdAt:desc";
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/transactions", { params });
  }

  public async getTransactionById(id: string): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/transactions/${id}`);
  }

  public async searchTransactions(params: {
    q?: string;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/transactions/search", { params });
  }

  public async creditOrganizationAccount(payload: {
    organizationId: number;
    amount: number; // Business rule: USD
    narration: string;
  }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/transactions/credit", payload);
  }

  public async reverseTransaction(payload: { transactionId: string; narration: string }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/transactions/reverse", payload);
  }

  // ========================================
  // ORGANIZATION ACCOUNT MANAGEMENT
  // ========================================
  public async getOrganizationAccounts(params?: {
    organizationId?: number;
    currencyId?: number;
    isDefault?: boolean;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/organization-accounts", { params });
  }

  public async getOrganizationAccount(organizationId: number): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/organizations/${organizationId}/account`);
  }

  public async createOrganizationAccount(
    organizationId: number,
    payload: {
      accountNumber: string;
      accountName: string;
      paymentInstitutionId: number; // Bank ID
      paymentInstitutionBranchId?: number; // Branch ID (optional)
      currencyId: number;
      isDefault?: boolean;
    }
  ): Promise<ApiResponse<any>> {
    return this.api.post(`/api/v1/organizations/${organizationId}/account`, payload);
  }

  public async updateOrganizationAccount(
    organizationId: number,
    accountId: number,
    payload: {
      accountNumber?: string;
      accountName?: string;
      paymentInstitutionId?: number;
      paymentInstitutionBranchId?: number;
      isDefault?: boolean;
    }
  ): Promise<ApiResponse<any>> {
    return this.api.patch(`/api/v1/organizations/${organizationId}/account/${accountId}`, payload);
  }

  // ========================================
  // STATEMENT REQUESTS
  // ========================================
  public async getStatementRequests(params?: {
    organizationId?: number;
    transactionType?: "CREDIT" | "DEBIT" | "REVERSAL" | "ALL";
    requestType?: "MANUAL" | "PERIODIC";
    includedRelations?: boolean;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/statement-requests", { params });
  }

  public async getStatementRequestById(id: number): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/statement-requests/${id}`);
  }

  public async createStatementRequest(payload: {
    organizationId: number;
    transactionType: "CREDIT" | "DEBIT" | "REVERSAL" | "ALL";
    requestType: "MANUAL" | "PERIODIC";
    startDate: string;
    endDate: string;
    recipientEmail: string;
  }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/statement-requests", payload);
  }

  public async updateStatementRequest(
    id: number,
    payload: {
      recipientEmail?: string;
      transactionType?: "CREDIT" | "DEBIT" | "REVERSAL" | "ALL";
      startDate?: string;
      endDate?: string;
    }
  ): Promise<ApiResponse<any>> {
    return this.api.patch(`/api/v1/statement-requests/${id}`, payload);
  }

  // ========================================
  // EXCHANGE RATES
  // ========================================
  public async getExchangeRates(params?: {
    fromCurrencyId?: number;
    toCurrencyId?: number;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/exchange-rates", { params });
  }

  public async getExchangeRateById(id: number): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/exchange-rates/${id}`);
  }

  public async createExchangeRate(payload: {
    fromCurrencyId: number;
    toCurrencyId: number;
    buyRate: number;
    sellRate: number;
  }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/exchange-rates", payload);
  }

  public async updateExchangeRate(
    id: number,
    payload: {
      buyRate?: number;
      sellRate?: number;
    }
  ): Promise<ApiResponse<any>> {
    return this.api.patch(`/api/v1/exchange-rates/${id}`, payload);
  }

  public async deleteExchangeRate(id: number): Promise<ApiResponse<any>> {
    return this.api.delete(`/api/v1/exchange-rates/${id}`);
  }

  public async convertCurrency(from: string, to: string, amount: number): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/exchange-rates/${from}/${to}`, {
      params: { amount },
    });
  }

  public async getExchangeRateHistory(params?: {
    fromCurrencyId?: number;
    toCurrencyId?: number;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/exchange-rates-history", { params });
  }

  // ========================================
  // PAYMENT CHANNELS
  // ========================================
  public async getPaymentChannels(params?: {
    countryId?: number;
    chargeType?: "FLAT" | "PERCENT";
    applyTax?: boolean;
    hasThirdPartyApiSupport?: boolean;
    active?: boolean;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/payment-channels", { params });
  }

  public async getPaymentChannelById(id: number): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/payment-channels/${id}`);
  }

  public async searchPaymentChannels(params: {
    q?: string;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/payment-channels/search", { params });
  }

  public async createPaymentChannel(payload: {
    countryId: number;
    code: string;
    name: string;
    description: string;
    chargeType: "FLAT" | "PERCENT";
    chargeRate: number;
    minimumCharge: number;
    maximumCharge?: number;
    applyTax: boolean;
    hasThirdPartyApiSupport: boolean;
  }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/payment-channels", payload);
  }

  public async updatePaymentChannel(
    id: number,
    payload: {
      countryId?: number;
      code?: string;
      name?: string;
      description?: string;
      chargeType?: "FLAT" | "PERCENT";
      chargeRate?: number;
      minimumCharge?: number;
      maximumCharge?: number;
      applyTax?: boolean;
      hasThirdPartyApiSupport?: boolean;
      active?: boolean;
    }
  ): Promise<ApiResponse<any>> {
    return this.api.patch(`/api/v1/payment-channels/${id}`, payload);
  }

  public async deletePaymentChannel(id: number): Promise<ApiResponse<any>> {
    return this.api.delete(`/api/v1/payment-channels/${id}`);
  }

  // ========================================
  // PAYMENT INSTITUTIONS
  // ========================================
  public async getPaymentInstitutions(params?: {
    shortName?: string;
    active?: boolean;
    code?: string;
    swiftCode?: string;
    paymentChannelType?: "OTHER" | "WALLET" | "BANK";
    countryId?: number;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/payment-institutions", { params });
  }

  public async getPaymentInstitutionById(id: number): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/payment-institutions/${id}`);
  }

  public async searchPaymentInstitutions(params: {
    q?: string;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/payment-institutions/search", { params });
  }

  public async createPaymentInstitution(payload: {
    shortName: string;
    name: string;
    active?: boolean;
    countryId: number;
    paymentChannelType: "OTHER" | "WALLET" | "BANK";
    code: string;
    swiftCode?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/payment-institutions", payload);
  }

  public async updatePaymentInstitution(
    id: number,
    payload: {
      shortName?: string;
      name?: string;
      active?: boolean;
      paymentChannelType?: "OTHER" | "WALLET" | "BANK";
      code?: string;
      swiftCode?: string;
    }
  ): Promise<ApiResponse<any>> {
    return this.api.patch(`/api/v1/payment-institutions/${id}`, payload);
  }

  public async deletePaymentInstitution(id: number): Promise<ApiResponse<any>> {
    return this.api.delete(`/api/v1/payment-institutions/${id}`);
  }

  // ========================================
  // PAYMENT INSTITUTION BRANCHES
  // ========================================
  public async getPaymentInstitutionBranches(params?: {
    paymentInstitutionId?: number;
    active?: boolean;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/payment-institution-branches", { params });
  }

  public async getPaymentInstitutionBranchById(id: number): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/payment-institution-branches/${id}`);
  }

  public async searchPaymentInstitutionBranches(params: {
    q?: string;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/payment-institution-branches/search", {
      params,
    });
  }

  public async createPaymentInstitutionBranch(payload: {
    paymentInstitutionId: number;
    name: string;
    sortCode: string;
    description: string;
  }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/payment-institution-branches", payload);
  }

  public async updatePaymentInstitutionBranch(
    id: number,
    payload: {
      paymentInstitutionId?: number;
      name?: string;
      sortCode?: string;
      description?: string;
      active?: boolean;
    }
  ): Promise<ApiResponse<any>> {
    return this.api.patch(`/api/v1/payment-institution-branches/${id}`, payload);
  }

  public async deletePaymentInstitutionBranch(id: number): Promise<ApiResponse<any>> {
    return this.api.delete(`/api/v1/payment-institution-branches/${id}`);
  }

  // ========================================
  // SERVICE CATEGORIES MANAGEMENT
  // ========================================
  public async getServiceCategories(params?: {
    type?: "END_USER" | "HOST";
    q?: string;
    page?: number;
    limit?: number;
    orderBy?:
      | "createdAt:asc"
      | "createdAt:desc"
      | "modifiedAt:asc"
      | "modifiedAt:desc"
      | "code:asc"
      | "code:desc"
      | "name:asc"
      | "name:desc";
  }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/service-categories", { params });
  }

  public async getAllServiceCategories(params?: { type?: "END_USER" | "HOST" }): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/service-categories/all", { params });
  }

  public async getServiceCategoryById(id: number): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/service-categories/${id}`);
  }

  public async createServiceCategory(payload: {
    name: string;
    code: string;
    type: "END_USER" | "HOST";
  }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/service-categories", payload);
  }

  public async updateServiceCategory(
    id: number,
    payload: {
      name?: string;
      code?: string;
      type?: "END_USER" | "HOST";
    }
  ): Promise<ApiResponse<any>> {
    return this.api.patch(`/api/v1/service-categories/${id}`, payload);
  }

  public async deleteServiceCategory(id: number): Promise<ApiResponse<any>> {
    return this.api.delete(`/api/v1/service-categories/${id}`);
  }

  // ========================================
  // SERVICE SUBCATEGORIES MANAGEMENT
  // ========================================
  public async getServiceSubcategories(
    categoryId: number,
    params?: {
      q?: string;
      page?: number;
      limit?: number;
      orderBy?:
        | "createdAt:asc"
        | "createdAt:desc"
        | "modifiedAt:asc"
        | "modifiedAt:desc"
        | "code:asc"
        | "code:desc"
        | "name:asc"
        | "name:desc";
    }
  ): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/service-categories/${categoryId}/subcategories`, { params });
  }

  public async getServiceSubcategoryById(categoryId: number, id: number): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/service-categories/${categoryId}/subcategories/${id}`);
  }

  public async createServiceSubcategory(
    categoryId: number,
    payload: {
      name: string;
      code: string;
    }
  ): Promise<ApiResponse<any>> {
    return this.api.post(`/api/v1/service-categories/${categoryId}/subcategories`, payload);
  }

  public async updateServiceSubcategory(
    categoryId: number,
    id: number,
    payload: {
      name?: string;
      code?: string;
    }
  ): Promise<ApiResponse<any>> {
    return this.api.patch(`/api/v1/service-categories/${categoryId}/subcategories/${id}`, payload);
  }

  public async deleteServiceSubcategory(categoryId: number, id: number): Promise<ApiResponse<any>> {
    return this.api.delete(`/api/v1/service-categories/${categoryId}/subcategories/${id}`);
  }

  // ========================================
  // PUBLIC SERVICE CATEGORIES (NO AUTH)
  // ========================================
  public async getPublicServiceCategories(params?: {
    q?: string;
    page?: number;
    limit?: number;
    orderBy?:
      | "createdAt:asc"
      | "createdAt:desc"
      | "modifiedAt:asc"
      | "modifiedAt:desc"
      | "code:asc"
      | "code:desc"
      | "name:asc"
      | "name:desc";
  }): Promise<ApiResponse<any>> {
    // Create a new axios instance without auth interceptors for public endpoints
    const publicApi = axios.create({
      baseURL: Constants.BILLING_API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    return publicApi.get("/api/v1/service-categories/registry", { params });
  }

  public async getPublicServiceSubcategories(
    categoryId: number,
    params?: {
      q?: string;
      page?: number;
      limit?: number;
      orderBy?:
        | "createdAt:asc"
        | "createdAt:desc"
        | "modifiedAt:asc"
        | "modifiedAt:desc"
        | "code:asc"
        | "code:desc"
        | "name:asc"
        | "name:desc";
    }
  ): Promise<ApiResponse<any>> {
    // Create a new axios instance without auth interceptors for public endpoints
    const publicApi = axios.create({
      baseURL: Constants.BILLING_API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    return publicApi.get(`/api/v1/service-categories/${categoryId}/subcategories/registry`, { params });
  }

  public async getPublicAllSubcategoriesRegistry(): Promise<ApiResponse<any>> {
    const publicApi = axios.create({
      baseURL: Constants.BILLING_API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    return publicApi.get("/api/v1/service-categories/all/registry");
  }

  // ========================================
  // REVENUE SHARE MANAGEMENT
  // ========================================
  public async getDefaultRevenueShare(): Promise<ApiResponse<any>> {
    return this.api.get("/api/v1/revenue-share/default");
  }

  public async setDefaultRevenueShare(payload: { commissionPercentage: number }): Promise<ApiResponse<any>> {
    return this.api.post("/api/v1/revenue-share/default", payload);
  }

  public async getOrganizationRevenueShare(organizationId: number): Promise<ApiResponse<any>> {
    return this.api.get(`/api/v1/revenue-share/organizations/${organizationId}`);
  }

  public async setOrganizationRevenueShare(
    organizationId: number,
    payload: {
      commissionPercentage: number;
    }
  ): Promise<ApiResponse<any>> {
    return this.api.post(`/api/v1/revenue-share/organizations/${organizationId}`, payload);
  }

  public async deleteOrganizationRevenueShare(organizationId: number): Promise<ApiResponse<any>> {
    return this.api.delete(`/api/v1/revenue-share/organizations/${organizationId}`);
  }

  // ========================================
  // REVENUE AGGREGATES
  // ========================================
  public async getOrganizationRevenueAggregates(
    organizationId: number,
    params: {
      startDate: string; // YYYY-MM-DD format
      endDate: string; // YYYY-MM-DD format
      period: "DAY" | "WEEK" | "MONTH";
    }
  ): Promise<
    ApiResponse<{
      data: Array<{
        periodStart: string; // ISO datetime
        total: string; // Revenue amount as string
      }>;
      pagination: {
        totalCount: number;
        totalPages: number;
        page: number;
        limit: number;
      };
    }>
  > {
    return this.api.get(`/api/v1/organizations/${organizationId}/revenue/aggregates`, { params });
  }

  // ========================================
  // PLATFORM REVENUE REPORTS (Admin)
  // ========================================
  public async getPlatformRevenueReport(params: {
    startDate: string; // YYYY-MM-DD format (UTC)
    endDate: string; // YYYY-MM-DD format (UTC)
  }): Promise<
    ApiResponse<{
      data: {
        collections: {
          saasSubscriptions: {
            currencies: Array<{
              currency: {
                id: number;
                code: string;
                symbol: string;
              };
              amount: string;
              count: number;
            }>;
            totalCount: number;
          };
          nonSaasSubscriptions: {
            currencies: Array<{
              currency: {
                id: number;
                code: string;
                symbol: string;
              };
              amount: string;
              count: number;
            }>;
            totalCount: number;
          };
          directBookings: {
            currencies: Array<{
              currency: {
                id: number;
                code: string;
                symbol: string;
              };
              amount: string;
              count: number;
            }>;
            totalCount: number;
          };
        };
        bookingRevenue: {
          directBookings: {
            currencies: Array<{
              currency: {
                id: number;
                code: string;
                symbol: string;
              };
              amount: string;
              count: number;
            }>;
            totalCount: number;
          };
          subscriptions: {
            currencies: Array<{
              currency: {
                id: number;
                code: string;
                symbol: string;
              };
              amount: string;
              count: number;
            }>;
            totalCount: number;
          };
          breakdown: Array<{
            totalCount: number;
            data: Array<{
              item: {
                id: number;
                name: string;
                code: string;
              };
              currencies: Array<{
                currency: {
                  id: number;
                  code: string;
                  symbol: string;
                };
                amount: string;
                count: number;
              }>;
              totalCount: number;
            }>;
            by: "serviceCategory" | "organizationType";
          }>;
        };
        revenueShare: {
          provider: Array<{
            currency: {
              id: number;
              code: string;
              symbol: string;
            };
            amount: string;
            count: number;
          }>;
          platform: Array<{
            currency: {
              id: number;
              code: string;
              symbol: string;
            };
            amount: string;
            count: number;
          }>;
          totalCount: number;
        };
      };
      pagination: {
        totalCount: number;
        totalPages: number;
        page: number;
        limit: number;
      };
    }>
  > {
    return this.api.get(`/api/v1/reports/revenue/platform/from/${params.startDate}/to/${params.endDate}`);
  }

  // ========================================
  // SYSTEM ENDPOINTS
  // ========================================
  public async healthCheck(): Promise<ApiResponse<any>> {
    return this.api.get("/healthcheck");
  }

  public async republishData(model: string): Promise<ApiResponse<any>> {
    return this.api.post(`/repub/${model}`);
  }
}

const billingService = new BillingService();
export default billingService;
